import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useAccessStatus } from "@/hooks/useAccessStatus";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
  requireSubscription?: boolean;
}

export const ProtectedRoute = ({
  children,
  requireOnboarding = true,
  requireSubscription = true,
}: ProtectedRouteProps) => {
  const { loading, isSignedIn } = useAuth();
  const { profile, isLoading: profileLoading, error: profileError } = useProfile();
  const { accessStatus, isLoading: accessLoading, error: accessError } = useAccessStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasRedirectedToOnboarding = useRef(false);

  const isProfileComplete = useMemo(() => {
    if (!profile) return false;
    // Profile is complete if they have completed onboarding (marked by onboarding_completed_at timestamp)
    return Boolean(profile.onboarding_completed_at);
  }, [profile]);

  // Reset the onboarding-redirect guard when the user signs out.
  useEffect(() => {
    if (!isSignedIn) {
      hasRedirectedToOnboarding.current = false;
    }
  }, [isSignedIn]);

  // Single ordered redirect decision: auth -> onboarding -> subscription.
  // Running these as three separate effects let multiple navigate() calls
  // fire in the same render pass and race each other. Every redirect uses
  // `replace` so the guarded route never lands in history (no back-button
  // trap), and the auth redirect carries the intended destination so the
  // user returns here after logging in.
  useEffect(() => {
    if (loading) return;

    // 1. Not signed in -> auth, preserving the intended destination.
    if (!isSignedIn) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/auth/sign-in?source=protected-route&next=${next}`, {
        replace: true,
        state: { from: location.pathname + location.search },
      });
      return;
    }

    // 2. Profile incomplete -> onboarding. Wait for the profile to load and
    //    skip during the error/bootstrap phase.
    if (
      requireOnboarding &&
      !profileLoading &&
      profile &&
      !isProfileComplete &&
      !profileError &&
      location.pathname !== "/onboarding" &&
      !hasRedirectedToOnboarding.current
    ) {
      hasRedirectedToOnboarding.current = true;
      navigate("/onboarding", { replace: true });
      return;
    }

    // 3. No active subscription -> gate. Dev bypass keeps localhost testers
    //    from being trapped; the production gate stays intact because
    //    import.meta.env.DEV is false in built bundles.
    //
    //    dealership_no_subscription: account exists but billing isn't active yet.
    //    → Let them into the dashboard. Individual features (train, call upload)
    //      have their own payment checks and will prompt to /activate on attempt.
    //    Everything else (no account, no dealership) → /pricing
    if (
      !import.meta.env.DEV &&
      requireSubscription &&
      !accessLoading &&
      accessStatus &&
      !accessStatus.allowed &&
      !accessStatus.isFounder &&
      accessStatus.reason !== 'dealership_no_subscription' &&
      location.pathname !== '/pricing'
    ) {
      navigate('/pricing', { replace: true });
    }
  }, [
    loading,
    isSignedIn,
    requireOnboarding,
    profileLoading,
    profile,
    isProfileComplete,
    profileError,
    requireSubscription,
    accessLoading,
    accessStatus,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Track when initial load is complete
  useEffect(() => {
    if (
      !loading &&
      !profileLoading &&
      isSignedIn &&
      (!requireSubscription || !accessLoading)
    ) {
      setHasLoadedOnce(true);
    }
  }, [loading, profileLoading, isSignedIn, requireSubscription, accessLoading]);

  // Determine if we're still in the loading phase
  const isLoadingPhase =
    loading ||
    (requireOnboarding && profileLoading) ||
    (requireSubscription && isSignedIn && accessLoading);

  // Only show loading screen on INITIAL load, never after (prevents unmounting during refetches)
  if (!hasLoadedOnce && isLoadingPhase) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-2xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            {profileLoading ? "Preparing your workspace..." : "Loading..."}
          </div>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return null; // useEffect will handle redirect
  }

  if (requireSubscription && accessError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <div className="text-2xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            Unable to verify access
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Refresh the page to retry your subscription check before continuing.
          </p>
        </div>
      </div>
    );
  }

  if (
    requireSubscription &&
    !import.meta.env.DEV &&
    (!accessStatus || (!accessStatus.allowed && !accessStatus.isFounder)) &&
    accessStatus?.reason !== 'dealership_no_subscription'
  ) {
    return null;
  }

  return <>{children}</>;
};
